import dayjs from 'dayjs';
import { MapUtils } from '../../utility/map-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/dto.js';
import { Country } from '../serializable/country.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { BusinessModuleType } from './business-module-type.js';
import { Language } from '../serializable/language.js';
import { TimeZoneRegion } from './time-zone-region.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { TimeZone } from '../../time/timezone.js';

export class BusinessModule extends DataModel {
  static readonly TYPE_ID: string = '227059f44e234334b025549f48a2f01c';

  readonly businessRef: BusinessModuleRef;

  constructor(
        public readonly uniqueId: string,
        public readonly creatorId: string,
        public readonly organizationId: string,
        public readonly moduleType: BusinessModuleType,
        public readonly name: string,
        public readonly creationDate: dayjs.Dayjs,
        public readonly country: Country,
        public readonly language: Language,
        public readonly timezone: TimeZoneRegion,
  ) {
    super();
    this.businessRef = BusinessModuleRef.create(uniqueId, organizationId, moduleType);
  }

  get $typeId(): string {
    return BusinessModule.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): BusinessModule {
    return new BusinessModule(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'creatorId'),
        MapUtils.get<string>(map, 'organizationId'),
        toEnumFromString(MapUtils.get<BusinessModuleType>(map, 'moduleType'), BusinessModuleType),
        MapUtils.get<string>(map, 'name'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<Country>(map, 'country'), Country),
        toEnumFromString(MapUtils.get<Language>(map, 'language'), Language),
        toEnumFromString(MapUtils.get<TimeZoneRegion>(map, 'timezone'), TimeZoneRegion),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'organizationId': this.organizationId,
      'moduleType': fromEnumAsString(this.moduleType),
      'name': this.name,
      'creationDate': context.encodeDate(this.creationDate),
      'country': fromEnumAsString(this.country),
      'language': fromEnumAsString(this.language),
      'timezone': fromEnumAsString(this.timezone),
    };
  }

  clone(): this {
    return new BusinessModule(
        this.uniqueId,
        this.creatorId,
        this.organizationId,
        this.moduleType,
        this.name,
        TimeZone.cloneDate(this.creationDate),
        this.country,
        this.language,
        this.timezone,
    ) as this;
  }

  isSame(other: BusinessModule): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
          other.businessRef === this.businessRef &&
          other.creatorId === this.creatorId &&
          other.organizationId === this.organizationId &&
          other.name === this.name &&
          other.creationDate === this.creationDate &&
          other.country === this.country &&
          other.language === this.language &&
          other.timezone === this.timezone &&
          other.moduleType == this.moduleType;
  }
}

export class BusinessModuleBuilder extends DataModelBuilder<BusinessModule> {
  build(context: SerializationContext, map: SerializedData): BusinessModule {
    return BusinessModule.fromMap(context, map);
  }
}
